/**
 * Device Management API Functions
 * 
 * Provides API functions for device management operations:
 * - Search device by MAC address
 * - Toggle device status (enable/disable)
 * - Get list of disabled devices
 * 
 * Currently using mock data for UI development
 * 
 * @author CTS - Ford PM UI Team
 */

import { axiosClient } from './axiosClient';
import { APIUrls } from '../constant/constant';
const { DEVICE_MANAGEMENT_ENDPOINTS } = APIUrls;

/**
 * Common error handler for device management API calls
 * @param {Error} error - The error object from axios
 * @param {Object} errorMessages - Custom error messages for different scenarios
 * @returns {void} - Throws appropriate error
 */
const handleApiError = (error, errorMessages) => {
  console.error(`${errorMessages.context} API error:`, error);
  
  const status = error.response?.status;
  const serverMessage = error.response?.data?.message;
  
  if (status === 404) {
    throw new Error(errorMessages.notFound || 'Resource not found');
  } else if (status === 400) {
    throw new Error(errorMessages.badRequest || 'Invalid request');
  } else if (status === 500) {
    throw new Error(errorMessages.serverError || 'Server error occurred');
  } else if (serverMessage) {
    throw new Error(serverMessage);
  } else {
    throw new Error(errorMessages.default || 'Operation failed. Please try again.');
  }
};

/**
 * Search for a device by MAC address
 * @param {string} deviceMac - Device MAC address to search for
 * @returns {Promise<Object>} Device information
 */
export const searchDeviceApi = async (deviceMac) => {
  try {
    const response = await axiosClient.get(DEVICE_MANAGEMENT_ENDPOINTS.SEARCH_DEVICE, {
      params: {
        device_mac: deviceMac
      }
    });
    
    console.log('Device search result:', response.data);
    return response.data;
  } catch (error) {
    handleApiError(error, {
      context: 'Search device',
      notFound: 'Device not found with the specified MAC address',
      badRequest: 'Invalid MAC address format',
      default: 'Failed to search device. Please try again.'
    });
  }
};


/**
 * Toggle device status (enable/disable) with selected action
 * @param {string} deviceMac - Device MAC address
 * @param {string} status - New status ('enabled' or 'disabled')
 * @param {string} selectedAction - Selected action to perform
 * @param {string} triggeredBy - Email of user triggering the action
 * @returns {Promise<Object>} API response
 */
export const toggleDeviceStatusApi = async (deviceMac, status, selectedAction, triggeredBy) => {
  try {
    const response = await axiosClient.post(DEVICE_MANAGEMENT_ENDPOINTS.GET_AVAILABLE_ACTIONS, {
      deviceMac: deviceMac,
      value: status === 'enabled' ? 'enable' : 'disable',
      comment: selectedAction,
      triggeredBy: triggeredBy
    });
    
    console.log('Toggle device status result:', response.data);
    return response.data;
  } catch (error) {
    handleApiError(error, {
      context: 'Toggle device status',
      notFound: 'Device not found for status toggle',
      badRequest: 'Invalid request for device status toggle',
      default: 'Failed to toggle device status. Please try again.'
    });
  }
};

/**
 * Get list of all disabled devices
 * @returns {Promise<Object>} Response containing devices array
 */
export const getDisabledDevicesApi = async () => {
  try {
    const response = await axiosClient.get(DEVICE_MANAGEMENT_ENDPOINTS.GET_DISABLED_DEVICES, {
      params: {
        all_sites: true
      }
    });
    
    console.log('Disabled devices result:', response.data);
    return response.data;
  } catch (error) {
    handleApiError(error, {
      context: 'Get disabled devices',
      notFound: 'No disabled devices found',
      serverError: 'Server error while fetching disabled devices',
      default: 'Failed to load disabled devices. Please try again.'
    });
  }
};

/**
 * Get device history by MAC address
 * @param {string} deviceMac - Device MAC address
 * @returns {Promise<Object>} Response containing device history array
 */
export const getDeviceHistoryApi = async (deviceMac) => {
  try {
    const response = await axiosClient.get(`${DEVICE_MANAGEMENT_ENDPOINTS.GET_DEVICE_HISTORY}/${deviceMac}`);
    console.log('Device history result:', response.data);
    return response.data;
  } catch (error) {
    handleApiError(error, {
      context: 'Get device history',
      notFound: 'No history found for this device',
      badRequest: 'Invalid device MAC address for history retrieval',
      default: 'Failed to load device history. Please try again.'
    });
  }
};
